import React, { createContext, useContext, useState, useEffect } from 'react';
import { agenciesAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { getImageUrl } from '../utils/helpers';

const WhiteLabelContext = createContext();

export const useWhiteLabel = () => {
  const context = useContext(WhiteLabelContext);
  if (!context) {
    throw new Error('useWhiteLabel must be used within a WhiteLabelProvider');
  }
  return context;
};

export const WhiteLabelProvider = ({ children }) => {
  const { user, isAdmin } = useAuth();
  const [agency, setAgency] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasWhiteLabeling, setHasWhiteLabeling] = useState(false);


  // Fetch agency data function
  const fetchAgency = async () => {
    if (!user || !isAdmin) {
      setLoading(false);
      return;
    }

    try {
      const response = await agenciesAPI.getMyAgency();
      const agencyData = response.data.agency;
      setAgency(agencyData);

      // Check if agency has white-labeling feature
      if (agencyData.plan_features) {
        const features = Array.isArray(agencyData.plan_features) 
          ? agencyData.plan_features 
          : (typeof agencyData.plan_features === 'string' 
              ? JSON.parse(agencyData.plan_features) 
              : []);
        
        const hasWhiteLabel = features.some(f => {
          const featureName = typeof f === 'string' ? f : (f.name || f);
          return featureName && (
            featureName.toLowerCase().includes('white') ||
            featureName.toLowerCase().includes('white-label') ||
            featureName.toLowerCase().includes('white_label') ||
            featureName.toLowerCase().includes('whitelabel')
          );
        });
        setHasWhiteLabeling(hasWhiteLabel);
        console.log('[WhiteLabel] Check result:', {
          hasWhiteLabel,
          agencyName: agencyData.name,
          agencyLogo: agencyData.logo_url,
          featuresCount: features.length,
          features: features.map(f => typeof f === 'string' ? f : (f.name || f))
        });
      } else {
        setHasWhiteLabeling(false);
        console.log('[WhiteLabel] No plan_features found');
      }
    } catch (error) {
      console.error('Error fetching agency data:', error);
      setAgency(null);
      setHasWhiteLabeling(false);
    } finally {
      setLoading(false);
    }
  };

  // Fetch agency data when user is admin
  useEffect(() => {
    fetchAgency();
  }, [user, isAdmin]);

  // Listen for refresh events
  useEffect(() => {
    const handleRefresh = () => {
      if (user && isAdmin) {
        fetchAgency();
      }
    };
    window.addEventListener('refreshWhiteLabel', handleRefresh);
    return () => {
      window.removeEventListener('refreshWhiteLabel', handleRefresh);
    };
  }, [user, isAdmin]);

  // Refresh agency data
  const refreshAgency = async () => {
    if (!user || !isAdmin) return;

    try {
      const response = await agenciesAPI.getMyAgency();
      const agencyData = response.data.agency;
      setAgency(agencyData);

      // Update white-labeling status
      if (agencyData.plan_features) {
        const features = Array.isArray(agencyData.plan_features) 
          ? agencyData.plan_features 
          : (typeof agencyData.plan_features === 'string' 
              ? JSON.parse(agencyData.plan_features) 
              : []);
        
        const hasWhiteLabel = features.some(f => {
          const featureName = typeof f === 'string' ? f : (f.name || f);
          return featureName && (
            featureName.toLowerCase().includes('white') ||
            featureName.toLowerCase().includes('white-label') ||
            featureName.toLowerCase().includes('white_label') ||
            featureName.toLowerCase().includes('whitelabel')
          );
        });
        setHasWhiteLabeling(hasWhiteLabel);
      } else {
        setHasWhiteLabeling(false);
      }
    } catch (error) {
      console.error('Error refreshing agency data:', error);
    }
  };

  const displayName = (hasWhiteLabeling && agency?.name) ? agency.name : 'sls';
  const displayLogo = (hasWhiteLabeling && agency?.logo_url) ? getImageUrl(agency.logo_url) : null;

  // Debug log
  if (hasWhiteLabeling) {
    console.log('[WhiteLabel] Final values:', {
      hasWhiteLabeling,
      agencyName: agency?.name,
      agencyLogo: agency?.logo_url,
      displayName,
      displayLogo,
      willUseAgencyName: hasWhiteLabeling && agency?.name,
      willUseAgencyLogo: hasWhiteLabeling && agency?.logo_url
    });
  }

  const value = {
    agency,
    loading,
    hasWhiteLabeling,
    refreshAgency,
    // Helper functions
    agencyName: agency?.name || 'sls',
    agencyLogo: agency?.logo_url || null,
    agencySlug: agency?.slug || null,
    // Use white-labeling if available, otherwise use default
    // Always use agency name/logo if they exist and white-labeling is enabled
    displayName,
    displayLogo
  };

  return (
    <WhiteLabelContext.Provider value={value}>
      {children}
    </WhiteLabelContext.Provider>
  );
};

export default WhiteLabelContext;
